import { HttpStatusCode, isAxiosError } from 'axios';
import { Result, err, ok } from 'neverthrow';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import axios from '@utils/axios';

import { Note } from 'types/Load';
import { ApiError } from 'types/api/ApiError';
import captureException from 'utils/captureException';

export type GlobalTranzNoteType = {
  id: number;
  value: string;
  description: string;
};

function handleNoteApiError(
  error: unknown,
  defaultMessage: string,
  functionName: string
): Result<never, ApiError> {
  captureException(error, { functionName });

  if (!isAxiosError(error)) {
    return err({ message: defaultMessage });
  }
  if (error.response?.status === HttpStatusCode.Unauthorized) {
    throw error;
  }
  if (error.message === 'Extension context invalidated.') {
    throw error;
  }

  const responseData = error.response?.data as
    { level?: ApiError['level']; message?: string } | undefined;

  return err({
    message: responseData?.message || defaultMessage,
    status: error.response?.status,
    level: responseData?.level,
  });
}

export async function getGlobalTranzNoteTypes(
  tmsID: number,
  quoteModeTypeEnum = 3
): Promise<Result<GlobalTranzNoteType[], ApiError>> {
  const params = new URLSearchParams({
    tmsID: String(tmsID),
    quoteModeTypeEnum: String(quoteModeTypeEnum),
  });

  try {
    const response = await axios.get<GlobalTranzNoteType[]>(
      `/load/options/globaltranz/note-types?${params.toString()}`
    );

    return ok(response.data);
  } catch (error) {
    return handleNoteApiError(
      error,
      'Failed to fetch note types',
      'getGlobalTranzNoteTypes'
    );
  }
}

export async function submitGlobalTranzNote(
  loadID: number,
  noteTypeID: number,
  text: string
): Promise<Result<Note[], ApiError>> {
  try {
    const response = await axios.post<Note[]>(`/load/${loadID}/note`, {
      note: text.trim(),
      noteTypeID,
    });

    return ok(response.data);
  } catch (error) {
    return handleNoteApiError(
      error,
      'Failed to submit note',
      'submitGlobalTranzNote'
    );
  }
}

export async function deleteGlobalTranzNote(
  loadID: number,
  noteID: number
): Promise<Result<Note[], ApiError>> {
  try {
    const response = await axios.delete<Note[]>(`/load/${loadID}/note`, {
      data: { noteId: noteID },
    });

    return ok(response.data);
  } catch (error) {
    return handleNoteApiError(
      error,
      'Failed to delete note',
      'deleteGlobalTranzNote'
    );
  }
}
